import * as React from 'react';
import { TextField, TextFieldProps, Tooltip } from '@mui/material';

/**
 * Props for {@link MTextField}.
 */
interface MTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'defaultValue'> {
    /**
     * Called with the new text value.
     *
     * @param value - The current input value.
     */
    onChange: (value: string) => void;
    /** Initial text value applied whenever it changes. */
    defaultValue?: string;
    /** Tooltip shown when hovering the field. */
    tooltip?: string;
}

/**
 * A MUI {@link TextField} wrapper that tracks its own value from
 * `defaultValue`/`onChange`, wraps the field in a {@link Tooltip}, and keeps
 * the label shrunk while focused or populated.
 */
const MTextField: React.FC<MTextFieldProps> = ({ onChange, defaultValue, tooltip, ...props }) => {
    const [textValue, setTextValue] = React.useState<string | undefined>();
    const [focused, setFocused] = React.useState(false);

    React.useEffect(() => {
        setTextValue(defaultValue);
    }, [defaultValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setTextValue(value);
        onChange(value);
    };
    const handleFocus = () => {
        setFocused(true);
    };

    const handleBlur = () => {
        setFocused(false);
    };

    return (
        <Tooltip title={tooltip ?? ''}>
            <TextField {...props}
                fullWidth={props.fullWidth !== undefined ? props.fullWidth : true}
                value={textValue}
                onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                slotProps={{
                    htmlInput: {
                        type: 'text',
                        autoComplete: 'on',
                        onFocus: handleFocus,
                        onBlur: handleBlur
                    },
                    inputLabel: {
                        shrink: focused || textValue !== undefined
                    }

                }}
            />
        </Tooltip>
    );
};

export default React.memo(MTextField);
